import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, History } from 'lucide-react';
import { get } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateOnly, dateTime, humanize } from '@/lib/utils';
import { Tabs } from '@/components/ui/layout';
import { Alert, Badge, Card, CardContent, SkeletonText } from '@/components/ui/primitives';

export type VisitScope = 'facility' | 'group' | 'platform';

export interface PastVisit {
  encounterId: string;
  facilityName: string | null;
  startedAt: string;
  closedAt: string | null;
  context: string;
  status: string;
  doctorName: string;
  departmentName: string | null;
  chiefComplaint: string | null;
  diagnoses: { icdCode: string; icdTitle: string; isPrimary: boolean }[];
  medicines: string[];
}

const SCOPE_LABEL: Record<VisitScope, string> = {
  facility: 'This hospital',
  group: 'Group branches',
  platform: 'Elsewhere on the platform',
};

const SCOPE_EMPTY: Record<VisitScope, string> = {
  facility: 'This is the first visit recorded here.',
  group: 'Nothing recorded for this patient at the other branches of this group.',
  platform: 'Nothing recorded for this patient at any other facility on the platform.',
};

function useVisits(patientId: string | undefined, scope: VisitScope, enabled = true) {
  return useQuery({
    queryKey: ['patient-visits', patientId, scope],
    queryFn: () => get<PastVisit[]>(`/patients/${patientId}/visits?scope=${scope}&limit=30`),
    enabled: Boolean(patientId) && enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function LastVisitCard({
  patientId,
  excludeEncounterId,
  onSeeAll,
}: {
  patientId: string;
  excludeEncounterId?: string;
  onSeeAll: () => void;
}) {
  const visits = useVisits(patientId, 'facility');
  const earlier = (visits.data ?? []).filter((visit) => visit.encounterId !== excludeEncounterId);
  const last = earlier[0];

  if (visits.isLoading || !last) return null;

  return (
    <Card className="mb-4">
      <CardContent className="pt-4 text-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="inline-flex items-center gap-2 font-medium">
            <History className="h-4 w-4" /> {tr('opd', 'last_visit')} {dateOnly(last.startedAt)}
          </span>
          <button
            type="button"
            onClick={onSeeAll}
            className="text-sm font-medium text-primary underline-offset-2 hover:underline focus-ring"
          >
            {earlier.length > 1 ? tr('opd', 'see_all_x_earlier_visits', { length: earlier.length }) : tr('opd', 'see_the_detail')}
          </button>
        </div>
        <VisitBody visit={last} className="mt-2" />
      </CardContent>
    </Card>
  );
}

export function PreviousVisits({
  patientId,
  excludeEncounterId,
}: {
  patientId: string;
  excludeEncounterId?: string;
}) {
  const { user } = useAuth();
  const [scope, setScope] = useState<VisitScope>('facility');

  const scopes: VisitScope[] = [
    'facility',
    ...(user?.shareHistoryWithinGroup ? (['group'] as VisitScope[]) : []),
    ...(user?.shareHistoryAcrossPlatform ? (['platform'] as VisitScope[]) : []),
  ];

  const visits = useVisits(patientId, scope);
  const rows = (visits.data ?? []).filter((visit) => visit.encounterId !== excludeEncounterId);

  return (
    <div className="space-y-3">
      {scopes.length > 1 && (
        <Tabs
          active={scope}
          onChange={(id) => setScope(id as VisitScope)}
          tabs={scopes.map((id) => ({ id, label: SCOPE_LABEL[id] }))}
        />
      )}

      {scope === 'platform' && (
        <p className="text-xs text-muted-foreground">
          {tr('opd', 'visits_recorded_at_facilities_outside_this')}
        </p>
      )}

      {visits.isLoading ? (
        <SkeletonText lines={3} />
      ) : visits.isError ? (
        <Alert variant="destructive">{describeError(visits.error) || tr('opd', 'those_visits_could_not_be_loaded')}</Alert>
      ) : rows.length === 0 ? (
        <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
          {SCOPE_EMPTY[scope]}
        </p>
      ) : (
        <VisitList visits={rows} />
      )}
    </div>
  );
}

function VisitList({ visits }: { visits: PastVisit[] }) {
  const [openId, setOpenId] = useState(visits[0]?.encounterId ?? '');

  return (
    <ol className="space-y-2">
      {visits.map((visit) => {
        const open = openId === visit.encounterId;
        const primary = visit.diagnoses.find((d) => d.isPrimary) ?? visit.diagnoses[0];
        return (
          <li key={visit.encounterId} className="rounded-lg border bg-card">
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpenId(open ? '' : visit.encounterId)}
              className="flex w-full items-start gap-2 p-3 text-start focus-ring"
            >
              {open ? (
                <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              )}
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{dateOnly(visit.startedAt)}</span>
                  <Badge variant="muted">{humanize(visit.context)}</Badge>
                  {visit.facilityName && <Badge variant="secondary">{visit.facilityName}</Badge>}
                  <span className="text-sm text-muted-foreground">{visit.doctorName}</span>
                </span>
                {!open && (
                  <span className="mt-0.5 block truncate text-sm text-muted-foreground">
                    {primary ? `${primary.icdCode} · ${primary.icdTitle}` : visit.chiefComplaint || tr('opd', 'no_diagnosis_recorded')}
                  </span>
                )}
              </span>
              <span className="hidden shrink-0 text-xs text-muted-foreground sm:block">
                {dateTime(visit.startedAt)}
              </span>
            </button>
            {open && <VisitBody visit={visit} className="border-t p-3" />}
          </li>
        );
      })}
    </ol>
  );
}

function VisitBody({ visit, className }: { visit: PastVisit; className?: string }) {
  return (
    <div className={className}>
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        <Line
          label={tr('opd', 'seen_by')}
          value={[visit.doctorName, visit.departmentName, visit.facilityName].filter(Boolean).join(' · ')}
        />
        <Line label={tr('opd', 'visit')} value={`${humanize(visit.context)} · ${humanize(visit.status)}`} />
        <Line label={tr('opd', 'complaint')} value={visit.chiefComplaint || '—'} />
        <Line
          label={tr('opd', 'diagnosis')}
          value={
            visit.diagnoses.length
              ? visit.diagnoses.map((d) => `${d.icdCode} ${d.icdTitle}${d.isPrimary ? ' (primary)' : ''}`).join(', ')
              : '—'
          }
        />
      </dl>
      {visit.medicines.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {visit.medicines.map((medicine, index) => (
            <Badge key={`${visit.encounterId}-${index}`} variant="secondary">
              {medicine}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="truncate">{value}</dd>
    </div>
  );
}
